import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { shimmer } from "./Autopilot.styled";
import { radius } from "@libertify/ui";

type ImgPhase = "hidden" | "skeleton" | "blurry" | "clear";

const BLURRY_DELAY = 500;
const CLEAR_DELAY = 800;

export const LandscapeMediaItem: React.FC<{
  mediaType: "image" | "video";
  src: string;
  startDelay: number;
  isDone: boolean;
  onReady: () => void;
}> = ({ mediaType, src, startDelay, isDone, onReady }) => {
  const [phase, setPhase] = useState<ImgPhase>("hidden");
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  });

  const startTimeRef = useRef(0);
  const completedRef = useRef(false);

  const doComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    setPhase("clear");
    onReadyRef.current();
  }, []);

  useEffect(() => {
    if (isDone) return;
    completedRef.current = false;
    startTimeRef.current = Date.now();
    let t2: ReturnType<typeof setTimeout>;
    let t3: ReturnType<typeof setTimeout>;
    const t1 = setTimeout(() => {
      setPhase("skeleton");
      t2 = setTimeout(() => {
        setPhase("blurry");
        t3 = setTimeout(doComplete, CLEAR_DELAY);
      }, BLURRY_DELAY);
    }, startDelay);

    const totalDuration = startDelay + BLURRY_DELAY + CLEAR_DELAY;
    // Catch up if the tab was backgrounded and the timeouts were throttled
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - startTimeRef.current >= totalDuration) {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        doComplete();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [startDelay, isDone, doComplete]);

  if (isDone)
    return (
      <LandscapeImgWrap>
        {mediaType === "image" ? (
          <LandscapeImg src={src} $blur={0} alt="" />
        ) : (
          <LandscapeVideo src={src} $blur={0} />
        )}
      </LandscapeImgWrap>
    );
  if (phase === "hidden") return null;
  if (phase === "skeleton") return <LandscapeShim />;
  return (
    <LandscapeImgWrap>
      {mediaType === "image" ? (
        <LandscapeImg src={src} $blur={phase === "blurry" ? 20 : 0} alt="" />
      ) : (
        <LandscapeVideo src={src} $blur={phase === "blurry" ? 20 : 0} />
      )}
    </LandscapeImgWrap>
  );
};

const LandscapeImgWrap = styled.div`
  width: 180px;
  height: 110px;
  border-radius: ${radius.box}px;
  overflow: hidden;
  flex-shrink: 0;
`;

const LandscapeImg = styled.img<{ $blur: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: blur(${(p) => p.$blur}px);
  transition: filter 3s ease;
`;

const LandscapeVideo = styled.video<{ $blur: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: blur(${(p) => p.$blur}px);
  transition: filter 3s ease;
`;

const LandscapeShim = styled.div`
  width: 180px;
  height: 110px;
  border-radius: ${radius.box}px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;
